import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, LogOut, Image, X, Settings, Save, Edit3, Check } from 'lucide-react';

const API = 'https://audio-craft-lab.preview.emergentagent.com/api';

const PhotoAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Upload form
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('urbain');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  
  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [personalPrice, setPersonalPrice] = useState(35);
  const [professionalPrice, setProfessionalPrice] = useState(95);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Edit photo
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'urbain', label: 'Urbain', color: '#00CCCC' },
    { id: 'portraits', label: 'Portraits', color: '#DD99CC' },
    { id: 'paysages', label: 'Paysages', color: '#00CC00' },
    { id: 'tournages', label: 'Tournages', color: '#FFB347' },
    { id: 'architecture', label: 'Architecture', color: '#87CEEB' },
  ];

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/admin/verify`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('adminPassword', password);
        fetchPhotos();
        fetchSettings();
      } else {
        setError('Mot de passe incorrect');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Check if already logged in
  useEffect(() => {
    const savedPassword = localStorage.getItem('adminPassword');
    if (savedPassword) {
      setPassword(savedPassword);
      verifyAndFetch(savedPassword);
    }
  }, []);

  const verifyAndFetch = async (pwd) => {
    try {
      const response = await fetch(`${API}/admin/verify`, {
        method: 'POST',
        headers: { 'X-Admin-Password': pwd }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        fetchPhotosWithPassword(pwd);
        fetchSettingsWithPassword(pwd);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPhotos = () => fetchPhotosWithPassword(password);
  const fetchSettings = () => fetchSettingsWithPassword(password);

  const fetchPhotosWithPassword = async (pwd) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/photos`, {
        headers: { 'X-Admin-Password': pwd }
      });
      if (response.ok) {
        const data = await response.json();
        setPhotos(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettingsWithPassword = async (pwd) => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        headers: { 'X-Admin-Password': pwd }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.licenses) {
          setPersonalPrice(data.licenses.personal?.price || 35);
          setProfessionalPrice(data.licenses.professional?.price || 95);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    localStorage.removeItem('adminPassword');
  };

  // File handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image');
        return;
      }
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !title || !category) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const params = new URLSearchParams({
        title,
        category,
        description
      });

      const response = await fetch(`${API}/admin/upload?${params}`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password },
        body: formData
      });

      if (response.ok) {
        const newPhoto = await response.json();
        setPhotos([newPhoto, ...photos]);
        setTitle('');
        setDescription('');
        setCategory('urbain');
        clearFile();
        setSuccess('Photo ajoutée avec succès !');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || "Erreur lors de l'upload");
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  // Delete
  const handleDelete = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;

    try {
      const response = await fetch(`${API}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': password }
      });

      if (response.ok) {
        setPhotos(photos.filter(p => p.photo_id !== photoId));
        setSuccess('Photo supprimée');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Save settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword && newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword && newPassword.length < 6) {
      setError('Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    setSavingSettings(true);

    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': password
        },
        body: JSON.stringify({
          personal_price: parseFloat(personalPrice),
          professional_price: parseFloat(professionalPrice),
          new_password: newPassword || null,
          current_password: password
        })
      });

      if (response.ok) {
        setSuccess('Paramètres sauvegardés !');
        if (newPassword) {
          setPassword(newPassword);
          localStorage.setItem('adminPassword', newPassword);
        }
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSavingSettings(false);
    }
  };

  // Start editing a photo
  const startEditPhoto = (photo) => {
    setEditingPhoto(photo.photo_id);
    setEditTitle(photo.title);
    setEditCategory(photo.category);
    setEditDescription(photo.description || '');
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingPhoto(null);
    setEditTitle('');
    setEditCategory('');
    setEditDescription('');
  };

  // Save photo edits
  const handleSaveEdit = async (photoId) => {
    setSavingEdit(true);
    setError('');

    try {
      const params = new URLSearchParams({
        title: editTitle,
        category: editCategory,
        description: editDescription
      });

      const response = await fetch(`${API}/photos/${photoId}?${params}`, {
        method: 'PUT',
        headers: { 'X-Admin-Password': password }
      });

      if (response.ok) {
        const updatedPhoto = await response.json();
        setPhotos(photos.map(p => p.photo_id === photoId ? updatedPhoto : p));
        setSuccess('Photo modifiée !');
        cancelEdit();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de la modification');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSavingEdit(false);
    }
  };

  // ==================== PAGE LOGIN ====================
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 pt-32">
        <div className="max-w-sm w-full">
          <h1 className="text-3xl font-black text-white text-center mb-8">Admin Photos</h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe admin"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white/50"
              autoFocus
            />
            
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#00CCCC] text-black font-bold rounded-lg hover:bg-[#00AAAA] transition-colors disabled:opacity-50"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ==================== PAGE ADMIN ====================
  return (
    <div className="min-h-screen bg-black text-white pt-28">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Admin Photos</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center gap-2 transition-colors ${showSettings ? 'text-[#00CCCC]' : 'text-gray-400 hover:text-white'}`}
          >
            <Settings className="w-4 h-4" />
            Paramètres
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </header>

      {/* Success/Error messages */}
      {success && (
        <div className="mx-6 mt-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="max-w-6xl mx-auto p-6">
        {/* Settings Panel */}
        {showSettings && (
          <div className="border border-white/20 rounded-2xl p-6 mb-8 bg-white/5">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Paramètres
            </h2>

            <form onSubmit={handleSaveSettings} className="space-y-6">
              {/* Tarifs */}
              <div>
                <h3 className="text-sm font-medium text-gray-300 mb-3">Tarifs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Licence Personnelle (€)</label>
                    <input
                      type="number"
                      value={personalPrice}
                      onChange={(e) => setPersonalPrice(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50"
                      min="1"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Licence Professionnelle (€)</label>
                    <input
                      type="number"
                      value={professionalPrice}
                      onChange={(e) => setProfessionalPrice(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50"
                      min="1"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Mot de passe */}
              <div>
                <h3 className="text-sm font-medium text-gray-300 mb-3">Changer le mot de passe</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50"
                      placeholder="Laisser vide pour ne pas changer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Confirmer le mot de passe</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50"
                      placeholder="Confirmer"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={savingSettings}
                className="px-6 py-2 bg-[#00CCCC] text-black font-bold rounded-lg hover:bg-[#00AAAA] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Sauvegarder
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Upload form */}
        <div className="border border-white/20 rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Ajouter une photo
          </h2>

          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={clearFile}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/80 rounded-full flex items-center justify-center hover:bg-black"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-64 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center hover:border-white/40 transition-colors"
                  >
                    <Image className="w-12 h-12 text-gray-500 mb-3" />
                    <span className="text-gray-400">Cliquez pour sélectionner une image</span>
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Titre *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50"
                    placeholder="Titre de la photo"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Catégorie *</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategory(cat.id)}
                        className="px-3 py-1.5 rounded-full text-sm font-medium transition-all"
                        style={{
                          backgroundColor: category === cat.id ? cat.color : 'rgba(255,255,255,0.1)',
                          color: category === cat.id ? '#000' : '#fff',
                          border: `1px solid ${category === cat.id ? cat.color : 'rgba(255,255,255,0.2)'}`
                        }}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-white/50 resize-none"
                    rows={3}
                    placeholder="Description (optionnel)"
                  />
                </div>

                <button
                  type="submit"
                  disabled={uploading || !selectedFile || !title}
                  className="w-full py-3 bg-[#00CCCC] text-black font-bold rounded-lg hover:bg-[#00AAAA] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Ajouter la photo
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Photos list */}
        <div>
          <h2 className="text-lg font-semibold mb-4">
            Mes photos ({photos.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((photo) => {
                const catInfo = categories.find(c => c.id === photo.category);
                const isEditing = editingPhoto === photo.photo_id;
                
                return (
                  <div
                    key={photo.photo_id}
                    className="rounded-xl overflow-hidden border border-white/10 bg-white/5"
                  >
                    <div className="relative">
                      <img
                        src={photo.thumbnail_url || photo.image_url}
                        alt={photo.title}
                        className="w-full aspect-video object-cover"
                      />
                      {!isEditing && (
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            onClick={() => startEditPhoto(photo)}
                            className="w-8 h-8 bg-blue-500/80 hover:bg-blue-500 rounded-full flex items-center justify-center transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(photo.photo_id)}
                            className="w-8 h-8 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3">
                      {isEditing ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/50"
                            placeholder="Titre"
                          />
                          <div className="flex flex-wrap gap-1">
                            {categories.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => setEditCategory(cat.id)}
                                className="px-2 py-1 rounded-full text-xs font-medium transition-all"
                                style={{
                                  backgroundColor: editCategory === cat.id ? cat.color : 'rgba(255,255,255,0.1)',
                                  color: editCategory === cat.id ? '#000' : '#fff',
                                }}
                              >
                                {cat.label}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/50 resize-none"
                            rows={2}
                            placeholder="Description (optionnel)"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(photo.photo_id)}
                              disabled={savingEdit}
                              className="flex-1 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                            >
                              {savingEdit ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Sauvegarder
                                </>
                              )}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-sm truncate">{photo.title}</p>
                          <p 
                            className="text-xs uppercase mt-1"
                            style={{ color: catInfo?.color || '#00CCCC' }}
                          >
                            {catInfo?.label || photo.category}
                          </p>
                          {photo.description && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{photo.description}</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Aucune photo pour le moment</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoAdmin;
